import { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { ApiError } from '@/api/client';
import { useSession } from '@/auth/SessionContext';
import { ScreenBackButton } from '@/components/ScreenBackButton';

type AgentRule = {
  id: string;
  title: string | null;
  content: string;
  sortOrder: number;
  isActive: boolean;
  /** none | compiling | hard | soft | error — ver `agentRuleSpecs` en el backend. */
  specStatus?: 'none' | 'compiling' | 'hard' | 'soft' | 'error';
  specSummary?: string | null;
  specPreview?: string[];
  specReason?: string | null;
};

const SPEC_BADGE: Record<NonNullable<AgentRule['specStatus']>, { label: string; color: string; bg: string }> = {
  hard: { label: 'Verificada', color: '#166534', bg: '#dcfce7' },
  compiling: { label: 'Interpretando…', color: '#92400e', bg: '#fef3c7' },
  soft: { label: 'Solo guía', color: '#475569', bg: '#e2e8f0' },
  error: { label: 'Sin interpretar', color: '#991b1b', bg: '#fee2e2' },
  none: { label: 'Sin interpretar', color: '#475569', bg: '#e2e8f0' },
};

function RuleSpecInfo({ rule }: { rule: AgentRule }) {
  const status = rule.specStatus ?? 'none';
  const badge = SPEC_BADGE[status];
  return (
    <div style={{ marginTop: '0.5rem' }}>
      <span
        style={{
          display: 'inline-block',
          fontSize: '0.72rem',
          fontWeight: 600,
          padding: '2px 8px',
          borderRadius: 999,
          color: badge.color,
          background: badge.bg,
        }}
        title={
          status === 'hard' ?
            'El sistema calcula y revisa esta regla en cada respuesta.'
          : 'El agente la recibe como instrucción, pero el sistema no puede revisarla con cálculo.'
        }
      >
        {badge.label}
      </span>
      {status === 'hard' && rule.specSummary ?
        <p className="pwa-muted" style={{ margin: '0.35rem 0 0', fontSize: '0.8rem' }}>
          Así la entiende el sistema: {rule.specSummary}
        </p>
      : null}
      {status === 'hard' && rule.specPreview?.length ?
        <ul className="pwa-muted" style={{ margin: '0.25rem 0 0', paddingLeft: '1.1rem', fontSize: '0.78rem' }}>
          {rule.specPreview.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      : null}
      {(status === 'soft' || status === 'error') && rule.specReason ?
        <p className="pwa-muted" style={{ margin: '0.35rem 0 0', fontSize: '0.78rem' }}>
          {rule.specReason}
        </p>
      : null}
    </div>
  );
}

const TITLE_MAX = 120;
const CONTENT_MAX = 4000;

export function AgentRulesPage() {
  const { agentId = '' } = useParams();
  const location = useLocation();
  const agentName =
    (location.state as { agentName?: string } | null)?.agentName?.trim() || 'Agente';
  const { get, post, patch, deleteJson, can } = useSession();
  const canWrite = can(['pwa.calendar.write', 'web.agents.write']);
  const [rules, setRules] = useState<AgentRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setErr(null);
    try {
      const rows = await get<AgentRule[]>(`/api/console/agents/${agentId}/rules`);
      setRules(rows);
    } catch (e) {
      if (!opts?.silent) setRules([]);
      setErr(e instanceof ApiError ? e.message : 'No se pudieron cargar las reglas');
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [agentId, get]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      void load();
    }, 0);
    return () => clearTimeout(t);
  }, [load]);

  // Mientras alguna regla se está interpretando, se refresca la lista hasta que termine.
  const anyCompiling = rules.some((rule) => rule.specStatus === 'compiling');
  useEffect(() => {
    if (!anyCompiling) return;
    const timer = window.setInterval(() => {
      void load({ silent: true });
    }, 3000);
    return () => window.clearInterval(timer);
  }, [anyCompiling, load]);

  function beginEdit(rule: AgentRule) {
    setEditingId(rule.id);
    setEditTitle(rule.title ?? '');
    setEditContent(rule.content);
    setEditActive(rule.isActive);
    setErr(null);
    setSaved(null);
  }

  async function createRule(title: string, content: string): Promise<boolean> {
    const trimmedTitle = title.trim();
    const trimmedContent = content.trim();
    if (!trimmedContent) {
      setErr('El texto de la regla es obligatorio.');
      return false;
    }
    if (trimmedTitle.length > TITLE_MAX || trimmedContent.length > CONTENT_MAX) {
      setErr('El título o el texto superan el máximo permitido.');
      return false;
    }
    setErr(null);
    setSaved(null);
    await post(`/api/console/agents/${agentId}/rules`, {
      title: trimmedTitle || null,
      content: trimmedContent,
      isActive: true,
    });
    return true;
  }

  async function addCustom() {
    setAdding(true);
    try {
      const created = await createRule(newTitle, newContent);
      if (!created) return;
      setNewTitle('');
      setNewContent('');
      setSaved('Regla agregada.');
      await load({ silent: true });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'No se pudo agregar la regla');
    } finally {
      setAdding(false);
    }
  }

  async function saveEdit(ruleId: string) {
    const trimmedContent = editContent.trim();
    if (!trimmedContent) {
      setErr('El texto de la regla es obligatorio.');
      return;
    }
    setBusyId(ruleId);
    setErr(null);
    setSaved(null);
    try {
      await patch(`/api/console/agents/${agentId}/rules/${ruleId}`, {
        title: editTitle.trim() || null,
        content: trimmedContent,
        isActive: editActive,
      });
      setEditingId(null);
      setSaved('Regla actualizada.');
      await load({ silent: true });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'No se pudo guardar la regla');
    } finally {
      setBusyId(null);
    }
  }

  async function removeRule(rule: AgentRule) {
    const label = rule.title?.trim() || 'esta regla';
    if (!window.confirm(`¿Borrar «${label}»?`)) return;
    setBusyId(rule.id);
    setErr(null);
    setSaved(null);
    try {
      await deleteJson(`/api/console/agents/${agentId}/rules/${rule.id}`, {});
      if (editingId === rule.id) setEditingId(null);
      setSaved('Regla borrada.');
      await load({ silent: true });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'No se pudo borrar la regla');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      <div className="pwa-screen-head">
        <ScreenBackButton to="/" ariaLabel="Volver al inicio" />
        <h1 className="pwa-screen-head__title">Reglas de {agentName}</h1>
      </div>

      {err ? <p className="pwa-err">{err}</p> : null}
      {saved ? <p className="pwa-muted">{saved}</p> : null}

      {canWrite ?
        <form
          className="pwa-card"
          style={{ marginBottom: '0.75rem' }}
          onSubmit={(event) => {
            event.preventDefault();
            void addCustom();
          }}
        >
          <h2 className="pwa-muted" style={{ fontSize: '0.95rem', fontWeight: 600, margin: '0 0 0.35rem' }}>
            Nueva regla
          </h2>
          <label className="pwa-muted" style={{ display: 'block', fontSize: '0.8rem' }}>
            Título
            <input
              className="pwa-field"
              value={newTitle}
              maxLength={TITLE_MAX}
              onChange={(e) => setNewTitle(e.target.value)}
            />
          </label>
          <label className="pwa-muted" style={{ display: 'block', fontSize: '0.8rem', marginTop: '0.75rem' }}>
            Texto
            <textarea
              className="pwa-field"
              rows={4}
              value={newContent}
              maxLength={CONTENT_MAX}
              onChange={(e) => setNewContent(e.target.value)}
            />
          </label>
          <button type="submit" className="pwa-btn pwa-btn--primary" style={{ marginTop: '0.85rem' }} disabled={adding}>
            {adding ? 'Agregando…' : 'Agregar regla'}
          </button>
        </form>
      : null}

      {loading ?
        <p className="pwa-muted">Cargando reglas…</p>
      : rules.length === 0 ?
        <p className="pwa-muted">Este agente todavía no tiene reglas.</p>
      : <ul style={{ listStyle: 'none', padding: 0, margin: '0.75rem 0 0' }}>
          {rules.map((rule) => (
            <li key={rule.id} className="pwa-card" style={{ marginBottom: '0.5rem' }}>
              {editingId === rule.id ?
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    void saveEdit(rule.id);
                  }}
                >
                  <label className="pwa-muted" style={{ display: 'block', fontSize: '0.8rem' }}>
                    Título
                    <input
                      className="pwa-field"
                      value={editTitle}
                      maxLength={TITLE_MAX}
                      onChange={(e) => setEditTitle(e.target.value)}
                    />
                  </label>
                  <label className="pwa-muted" style={{ display: 'block', fontSize: '0.8rem', marginTop: '0.75rem' }}>
                    Texto
                    <textarea
                      className="pwa-field"
                      rows={5}
                      value={editContent}
                      maxLength={CONTENT_MAX}
                      onChange={(e) => setEditContent(e.target.value)}
                    />
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: '0.85rem' }}>
                    <input
                      type="checkbox"
                      checked={editActive}
                      onChange={(e) => setEditActive(e.target.checked)}
                    />
                    Activa
                  </label>
                  <div style={{ display: 'flex', gap: 8, marginTop: '0.85rem' }}>
                    <button type="submit" className="pwa-btn pwa-btn--primary pwa-btn--sm" disabled={busyId === rule.id}>
                      {busyId === rule.id ? 'Guardando…' : 'Guardar'}
                    </button>
                    <button
                      type="button"
                      className="pwa-btn pwa-btn--secondary pwa-btn--sm"
                      disabled={busyId === rule.id}
                      onClick={() => setEditingId(null)}
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              : <>
                  <div style={{ fontWeight: 600 }}>
                    {rule.title?.trim() || 'Sin título'}
                    {!rule.isActive ? <span className="pwa-muted"> · inactiva</span> : null}
                  </div>
                  <p style={{ margin: '0.4rem 0 0', whiteSpace: 'pre-wrap' }}>{rule.content}</p>
                  <RuleSpecInfo rule={rule} />
                  {canWrite ?
                    <div style={{ display: 'flex', gap: 8, marginTop: '0.75rem' }}>
                      <button
                        type="button"
                        className="pwa-btn pwa-btn--secondary pwa-btn--sm"
                        disabled={busyId === rule.id}
                        onClick={() => beginEdit(rule)}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        className="pwa-btn pwa-btn--secondary pwa-btn--sm"
                        disabled={busyId === rule.id}
                        onClick={() => void removeRule(rule)}
                      >
                        {busyId === rule.id ? 'Borrando…' : 'Borrar'}
                      </button>
                    </div>
                  : null}
                </>}
            </li>
          ))}
        </ul>}

      {!canWrite && !loading && rules.length > 0 ?
        <p className="pwa-muted">Podés ver las reglas, pero no editarlas.</p>
      : null}
    </div>
  );
}
